import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Txt, useToast } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { SESSION_STORAGE_VARIABLES, sessionStorageManager } from '@app/services/storageManager';
import { useNavigate, useScreenResolution, useShouldShowClickToDisconnect } from '@app/hooks';
import { getPortfolio, selectDisconnectInternet, selectPortfolio, selectUserInfo } from '@app/redux';

import { DisconnectPageLayout } from '../../containers';
import { DisconnectPageNavigationProps } from '../../index';
import DisconnectCart from '../DisconnectCart';
import LeftSection from './LeftSection';

import styles from './index.module.scss';

const DisconnectStep1 = ({
  handleContinue,
  errorHandling,
}: {
  handleContinue: () => void;
  errorHandling: DisconnectPageNavigationProps['errorHandling'];
}) => {
  const dispatch = useDispatch();
  const { goBack, replace } = useNavigate();
  const { t } = useTranslation('Disconnect');
  const { addToast } = useToast();
  const toastDurationInMs = 5000;

  const { wasUpdateSuccessful, error } = useSelector(selectUserInfo);
  const { success: hasCancelledPlan } = useSelector(selectDisconnectInternet);
  const { portfolio } = useSelector(selectPortfolio);

  const { isSmall, isExtraSmall } = useScreenResolution();
  const isMobile = isSmall || isExtraSmall;
  const showClickToDisconnect = useShouldShowClickToDisconnect();

  useEffect(() => {
    if (hasCancelledPlan) {
      sessionStorageManager.setItem(SESSION_STORAGE_VARIABLES.SHOW_PLAN_TAB, true);
      replace('Profile');
    }
  }, [hasCancelledPlan, replace]);

  useEffect(() => {
    if (wasUpdateSuccessful) {
      addToast({
        header: t('contactInfo.toast.success.header'),
        body: t('contactInfo.toast.success.body'),
        variant: 'success',
        duration: toastDurationInMs,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wasUpdateSuccessful]); // Only trigger on successful update

  useEffect(() => {
    if (error) {
      addToast({
        header: t('contactInfo.toast.error.header'),
        body: t('contactInfo.toast.error.body'),
        variant: 'error',
        duration: toastDurationInMs,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]); // Only trigger on error

  useEffect(() => {
    if (!portfolio) dispatch(getPortfolio());
  }, [portfolio, dispatch]);

  if (!showClickToDisconnect) {
    //should the customer try to type in their browser the /Disconnect route
    //check whether or not they live in a state eligible for click 2 disconnect
    goBack();
    return null;
  }

  const handleCancel = () => {
    sessionStorageManager.setItem(SESSION_STORAGE_VARIABLES.SHOW_PLAN_TAB, true);
    goBack();
  };

  return (
    <DisconnectPageLayout
      step={1}
      header={
        <div className={styles['title']}>
          <Txt variant={isMobile ? 'heading5' : 'heading3'}>{t('page.title.intro')}</Txt>
        </div>
      }
      leftContent={
        <LeftSection handleCancel={handleCancel} handleContinue={handleContinue} errorHandling={errorHandling} />
      }
      rightContent={<DisconnectCart />}
    />
  );
};

export default DisconnectStep1;
